/**
 * Enhances the Reply action on recital posts:
 *
 * 1. Published recitals (performance posts): Replaces the default Reply link
 *    with one that pre-fills the composer with a mention including the piece title.
 *    e.g., `> @"Sophia"#p1234 — Clair de Lune`
 *
 * 2. Submission threads: Replaces the default Reply link with one that doesn't
 *    include a post mention (submissions are private, mentions would fail).
 *
 * Also adds CSS classes:
 * - `.Post--performance` on performance posts (has Performer + Piece fields)
 * - `.Post--submission` on submission posts in active submission threads
 *
 * These classes are used to hide the default flarum/mentions Reply link via CSS,
 * since we can't patch flarum/mentions' ES module exports directly.
 */
import { extend } from 'flarum/common/extend';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import { getSubmissionTagIds } from './recitalTypes';
function extractPiece(html) {
  var marker = 'Piece:</strong>';
  var idx = html.indexOf(marker);
  if (idx === -1) return '';
  var afterMarker = html.substring(idx + marker.length);
  var endIdx = afterMarker.indexOf('<');
  if (endIdx === -1) return '';
  return afterMarker.substring(0, endIdx).trim();
}
function isPerformancePost(post) {
  if (!post || post.number() <= 1) return false;
  var contentHtml = post.contentHtml ? post.contentHtml() : '';
  return contentHtml.indexOf('Performer:</strong>') !== -1 && contentHtml.indexOf('Piece:</strong>') !== -1;
}
export default function addAutoReplyTemplate() {
  // CSS to hide the mentions Reply on performance posts and submission posts
  var style = document.createElement('style');
  style.textContent = '.Post--performance .item-reply { display: none !important; } .Post--submission .item-reply { display: none !important; }';
  document.head.appendChild(style);
  extend(CommentPost.prototype, 'classes', function (classes) {
    var post = this.attrs.post;
    if (post && post.discussion()) {
      var discussion = post.discussion();
      var tags = discussion.tags();
      if (tags) {
        var recitalTagId = app.forum.attribute('recitalsRecitalTagId');
        var submissionTagIds = getSubmissionTagIds();
        var hasRecitalTag = recitalTagId && tags.some(function (t) {
          return t && String(t.id()) === String(recitalTagId);
        });
        var hasSubmissionTag = submissionTagIds.length > 0 && tags.some(function (t) {
          return t && submissionTagIds.indexOf(String(t.id())) !== -1;
        });
        if (hasRecitalTag && !hasSubmissionTag && isPerformancePost(post)) {
          classes.push('Post--performance');
        }
        if (hasSubmissionTag && post.number() > 1) {
          classes.push('Post--submission');
        }
      }
    }
  });
  extend(CommentPost.prototype, 'actionItems', function (items) {
    var post = this.attrs.post;
    if (!post || !post.discussion()) return;
    var discussion = post.discussion();
    var tags = discussion.tags();
    if (!tags) return;
    var recitalTagId = app.forum.attribute('recitalsRecitalTagId');
    var submissionTagIds = getSubmissionTagIds();
    var hasRecitalTag = recitalTagId && tags.some(function (t) {
      return t && String(t.id()) === String(recitalTagId);
    });
    var hasSubmissionTag = submissionTagIds.length > 0 && tags.some(function (t) {
      return t && submissionTagIds.indexOf(String(t.id())) !== -1;
    });
    if (!discussion.canReply() || !app.session.user) return;

    // Case 1: Performance post in published recital
    if (hasRecitalTag && !hasSubmissionTag && isPerformancePost(post)) {
      var user = post.user();
      var username = user ? user.displayName() : 'Unknown';
      var postId = post.id();
      var contentHtml = post.contentHtml ? post.contentHtml() : '';
      var piece = extractPiece(contentHtml);
      items.add('recitalReply', m('button', {
        className: 'Button Button--link',
        onclick: function (e) {
          e.preventDefault();
          DiscussionControls.replyAction.call(discussion).then(function (composer) {
            var mention = '> @"' + username + '"#p' + postId;
            if (piece) {
              mention += ' \u2014 ' + piece;
            }
            mention += '\n\n';
            try {
              if (!composer.fields.content()) {
                composer.body.attrs.originalContent = mention;
              }
              composer.editor.insertAtCursor(mention, false);
            } catch (e) {
              // Fallback: set content directly
              composer.fields.content(mention);
            }
          });
        }
      }, app.translator.trans('flarum-mentions.forum.post.reply_link')));
    }

    // Case 2: Post in submission thread — plain Reply (template auto-fills via addSubmissionTemplate)
    if (hasSubmissionTag && post.number() > 1) {
      items.add('submissionReply', m('button', {
        className: 'Button Button--link',
        onclick: function (e) {
          e.preventDefault();
          DiscussionControls.replyAction.call(discussion);
        }
      }, app.translator.trans('flarum-mentions.forum.post.reply_link')));
    }
  });
}